<script setup>
import Header from './Header.vue';
import Footer from './Footer.vue';
import { ref, onMounted } from 'vue';
import { request } from '@/lib/api';
import { useToast } from '@/components/ui/toast';
import { Loader2 } from 'lucide-vue-next';
import SEO from '@/components/SEO.vue';

const { toast } = useToast();
const blogPosts = ref([]);
const regularPosts = ref([]);
const isLoading = ref(true);

const fetchBlogPosts = async () => {
  try {
    const data = await request('/blog');
    const list = data.map((post) => ({
      ...post,
      date: new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    }));

    blogPosts.value = [...list];

    if (list.length > 0) {
      regularPosts.value = [...list];
    }
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    toast({
      title: 'Error',
      description: 'Failed to load blog posts. Please try again later.',
      variant: 'destructive',
    });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchBlogPosts();
});
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-[#f8f9fa] to-[#f0f2f5]">
    <!-- Header Section -->
    <Header />

    <!-- SEO Section -->
    <SEO
      title="Seede AI Blog - Insights and Updates"
      description="Explore the latest insights, tutorials, and updates from the Seede AI team on our official blog."
      :keywords="['AI', 'Design', 'Blog', 'Tutorials', 'Seede AI'].join(',')"
    />

    <!-- Blog Section -->
    <section class="container mx-auto px-4 pt-28 pb-16">
      <div class="max-w-6xl mx-auto">
        <h1 class="text-4xl sm:text-5xl font-bold mb-6 text-center">Our Blog</h1>
        <p class="text-black/60 text-center text-lg mb-12 max-w-2xl mx-auto">
          Insights, tutorials, and updates from the Seede AI team
        </p>

        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-20">
          <Loader2 class="h-12 w-12 animate-spin text-black/40" />
        </div>

        <!-- Regular Posts Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <router-link v-for="post in regularPosts" :key="post.id" :to="`/blog/${post.id}`">
            <div
              class="bg-white rounded-xl overflow-hidden shadow-md border border-black/5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <div class="h-48 overflow-hidden">
                <img
                  :src="post.image || 'https://via.placeholder.com/400x300'"
                  :alt="post.title"
                  class="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div class="p-6">
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="bg-black/5 text-black/80 text-xs font-medium px-2.5 py-0.5 rounded-full"
                    >{{ post.category }}</span
                  >
                  <span class="text-black/50 text-xs">{{ post.date }}</span>
                </div>
                <h3
                  class="text-xl font-bold text-black/90 mb-3 hover:text-blue-600 transition-colors min-h-[4rem] break-all line-clamp-2 flex items-center"
                >
                  {{ post.title }}
                </h3>
                <p class="text-black/60 text-sm mb-4 line-clamp-2">
                  {{ post.excerpt }}
                </p>
                <div class="flex items-center justify-between mt-4 pt-4 border-t border-black/5">
                  <div class="flex items-center">
                    <div
                      class="w-7 h-7 bg-gradient-to-br from-black/40 to-black/60 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    >
                      {{ post.author.charAt(0) }}
                    </div>
                    <span class="ml-2 text-xs text-black/60">{{ post.author }}</span>
                  </div>
                  <span class="text-xs text-black/50">{{ post.readTime }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Footer Section -->
    <Footer />
  </div>
</template>
